import React, { useState, useEffect } from 'react';
import { obtenerUrlProtegida } from '../utils/archivos';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    AppBar,
    Toolbar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    CircularProgress,
    styled
} from '@mui/material';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faEye,
    faFilePdf,
    faDownload,
    faFileUpload,
    faFile
} from '@fortawesome/free-solid-svg-icons';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.hmsaa.com.mx:5001';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

function FileUploadField({ label, value, onFileChange, onPreview }) {
    const fileName = value?.name || (typeof value === 'string' ? value.split('/').pop().replace(/^[0-9a-f]{12}_/, '') : '');

    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
                {label}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                    component="label"
                    variant="outlined"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faFileUpload} />}
                >
                    Seleccionar archivo
                    <VisuallyHiddenInput
                        type="file"
                        accept="application/pdf"
                        onChange={onFileChange}
                    />
                </Button>

                {fileName && (
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FontAwesomeIcon icon={value instanceof File ? faFilePdf : faFile}
                            color={value instanceof File ? "#FF0000" : "inherit"} />
                        {fileName}
                    </Typography>
                )}

                {!fileName && (
                    <Typography variant="body2" color="textSecondary">
                        Ningún archivo seleccionado
                    </Typography>
                )}
            </Box>

            {value && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FontAwesomeIcon icon={faEye} />}
                        onClick={() => onPreview(value)}
                        disabled={value instanceof File} // Deshabilitar si es un nuevo archivo (aún no subido)
                    >
                        Previsualizar
                    </Button>
                </Box>
            )}
        </Box>
    );
}

function ContratosTable() {
    const [contratos, setContratos] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [proveedoresMap, setProveedoresMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentRow, setCurrentRow] = useState(null);
    const [newContrato, setNewContrato] = useState({
        id_proveedor: '',
        archivo_pdf: null
    });

    const verFacturaPdf = async (factura) => {
        try {
            const token = localStorage.getItem("access_token");

            const res = await fetch(
                `${API_BASE}/facturas/${factura.id}/pdf`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!res.ok) {
                throw new Error("No se pudo cargar el PDF");
            }

            const blob = await res.blob();
            const pdfUrl = URL.createObjectURL(blob);

            Swal.fire({
                title: "Vista previa de la factura",
                html: `
        <div style="height:70vh;">
          <iframe 
            src="${pdfUrl}" 
            style="width:100%; height:100%; border:none;"
          ></iframe>
        </div>
      `,
                width: "80%",
                showConfirmButton: true,
                confirmButtonText: "Descargar",
                showCancelButton: true,
                cancelButtonText: "Cerrar",
                didConfirm: () => {
                    const a = document.createElement("a");
                    a.href = pdfUrl;
                    a.download = `factura_${factura.uuid}.pdf`;
                    a.click();
                },
                willClose: () => {
                    URL.revokeObjectURL(pdfUrl);
                }
            });

        } catch (err) {
            console.error(err);
            Swal.fire("Error", "No se pudo abrir la factura", "error");
        }
    };

    useEffect(() => {
        const cargarDatos = async () => {
            try {
                const [contratosRes, proveedoresRes] = await Promise.all([
                    fetch(`${API_URL}/api/contratos`),
                    fetch(`${API_URL}/api/proveedores`)
                ]);

                if (!contratosRes.ok || !proveedoresRes.ok) throw new Error('Error en la carga de datos');

                const [contratosData, proveedoresData] = await Promise.all([
                    contratosRes.json(),
                    proveedoresRes.json()
                ]);

                const contratosValidados = contratosData.map(c => ({
                    ...c,
                    id_contrato: Number(c.id_contrato),
                    id_proveedor: Number(c.id_proveedor)
                }));

                const proveedoresValidados = proveedoresData.map(p => ({
                    ...p,
                    id_proveedor: Number(p.id)
                }));

                setContratos(contratosValidados);
                setProveedores(proveedoresValidados);

                const map = proveedoresValidados.reduce((acc, curr) => {
                    acc[curr.id_proveedor] = curr;
                    return acc;
                }, {});

                setProveedoresMap(map);

            } catch (error) {
                console.error('Error:', error);
                setError('Error al cargar los datos');
                Swal.fire('Error', 'No se pudieron cargar los datos', 'error');
            } finally {
                setLoading(false);
            }
        };
        cargarDatos();
    }, []);

    const handlePreview = async (url) => {
        if (!url) {
            Swal.fire('Error', 'No hay archivo disponible para previsualizar.', 'error');
            return;
        }

        let fileUrl;
        try {
            fileUrl = await obtenerUrlProtegida(`${API_URL}/uploads/${url}`);
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
            return;
        }

        Swal.fire({
            title: 'Vista previa del contrato',
            html: `
                <div style="position: relative; height: 70vh;">
                    <iframe 
                        src="${fileUrl}" 
                        style="width: 100%; height: 100%; border: none;"
                    ></iframe>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Descargar',
            cancelButtonText: 'Cerrar',
            width: '80%',
            customClass: {
                popup: 'preview-modal',
                content: 'preview-content'
            },
            didOpen: () => {
                Swal.getConfirmButton().addEventListener('click', () => {
                    window.open(fileUrl, '_blank');
                });
            },
            didClose: () => {
                setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
            }
        });
    };

    const handleSaveRow = async () => {
        if (!newContrato.archivo_pdf) {
            Swal.fire('Error', 'Debes seleccionar un archivo PDF', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('id_proveedor', newContrato.id_proveedor);
        formData.append('archivo_pdf', newContrato.archivo_pdf);

        try {
            const url = editMode
                ? `${API_URL}/api/contratos/${currentRow.id_contrato}`
                : `${API_URL}/api/contratos`;

            const response = await fetch(url, {
                method: editMode ? 'PUT' : 'POST',
                body: formData
            });

            if (!response.ok) throw new Error(await response.text());

            const contratoActualizado = await response.json();

            setContratos(prev => editMode
                ? prev.map(item => item.id_contrato === contratoActualizado.id_contrato ? contratoActualizado : item)
                : [contratoActualizado, ...prev]
            );

            Swal.fire('Éxito', `Contrato ${editMode ? 'actualizado' : 'creado'} correctamente`, 'success');
            setDialogOpen(false);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message || 'Operación fallida', 'error');
        }
    };

    const handleDeleteRow = async (id) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        });

        if (!isConfirmed) return;

        try {
            const response = await fetch(`${API_URL}/api/contratos/${id}`, { method: 'DELETE' });
            if (!response.ok) throw new Error(await response.text());

            setContratos(prev => prev.filter(item => item.id_contrato !== id));
            Swal.fire('Éxito', 'Contrato eliminado', 'success');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message || 'Error al eliminar', 'error');
        }
    };

    const contratosFiltrados = contratos.filter(contrato => {
        const proveedor = proveedoresMap[contrato.id_proveedor];
        return proveedor?.razon_social?.toLowerCase().includes(search.trim().toLowerCase());
    });

    const limpiarFiltros = () => setSearch('');

    if (loading) return (
        <Box textAlign="center" mt={5}>
            <CircularProgress />
            <Typography>Cargando...</Typography>
        </Box>
    );

    if (error) return (
        <Box textAlign="center" mt={5}>
            <Typography color="error">{error}</Typography>
        </Box>
    );

    return (
        <div>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Gestión de Contratos
                    </Typography>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => {
                            setNewContrato({ id_proveedor: '', archivo_pdf: null });
                            setEditMode(false);
                            setDialogOpen(true);
                        }}
                    >
                        Nuevo Contrato
                    </Button>
                </Toolbar>
            </AppBar>

            <BarraFiltros
                busqueda={search}
                onBusqueda={setSearch}
                placeholder="Buscar por proveedor"
                total={contratos.length}
                mostrados={contratosFiltrados.length}
                onLimpiar={limpiarFiltros}
            />

            <TableContainer component={Paper} sx={{ margin: '20px auto', maxWidth: 1200 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Proveedor</StyledTableCell>
                            <StyledTableCell>Documento</StyledTableCell>
                            <StyledTableCell>Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contratosFiltrados.map((contrato) => (
                            <TableRow key={contrato.id_contrato}>
                                <TableCell>
                                    {proveedoresMap[contrato.id_proveedor]?.razon_social || `Proveedor no encontrado (ID: ${contrato.id_proveedor})`}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="info"
                                        startIcon={<FontAwesomeIcon icon={faEye} />}
                                        onClick={() => handlePreview(contrato.archivo_pdf)}
                                        disabled={!contrato.archivo_pdf}
                                    >
                                        {contrato.archivo_pdf ? 'Ver Contrato' : 'Sin documento'}
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<FontAwesomeIcon icon={faEdit} />}
                                            onClick={() => {
                                                setCurrentRow(contrato);
                                                setNewContrato({
                                                    id_proveedor: contrato.id_proveedor,
                                                    archivo_pdf: contrato.archivo_pdf
                                                });
                                                setEditMode(true);
                                                setDialogOpen(true);
                                            }}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={<FontAwesomeIcon icon={faTrash} />}
                                            onClick={() => handleDeleteRow(contrato.id_contrato)}
                                        >
                                            Eliminar
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {contratosFiltrados.length === 0 && (
                            <SinResultados colSpan={3} total={contratos.length} onLimpiar={limpiarFiltros} entidad="contratos" />
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
                    {editMode ? 'Editar Contrato' : 'Nuevo Contrato'}
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 3 }}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Proveedor</InputLabel>
                        <Select
                            value={newContrato.id_proveedor}
                            onChange={(e) => setNewContrato({ ...newContrato, id_proveedor: e.target.value })}
                            label="Proveedor"
                        >
                            {proveedores.map(proveedor => (
                                <MenuItem key={proveedor.id_proveedor} value={proveedor.id_proveedor}>
                                    {proveedor.razon_social}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FileUploadField
                        label="Documento del Contrato (PDF)"
                        value={newContrato.archivo_pdf}
                        onFileChange={(e) => setNewContrato({ ...newContrato, archivo_pdf: e.target.files[0] })}
                        onPreview={handlePreview}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        color="secondary"
                        sx={{ textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSaveRow}
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none' }}
                    >
                        {editMode ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ContratosTable;